import React, { useState, useEffect } from 'react';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import { useUserCreditsContext } from 'contexts/UserCreditsContext/UserCreditsContext';
import { attemptedActionStore } from 'helpers/attemptedActionStore';
import { useStoreAttemptedDownloadAction } from 'hooks/useStoreAttemptedDownloadAction';
import { withInteractionSource } from 'contexts/InteractionSourceContext';
import { InteractionSource } from 'utils/analytics/AnalyticsV2Service.generated';
import { ModalProps, ModalCloseHandler } from 'modals/Modal.types';
import { useSweetenerUpsellModalContext } from 'modals/SweetenerUpsellModal/SweetenerUpsellModalContext';
import SplitModal from './components/SplitModal/SplitModal';
import DescriptivePaidUpsell from './components/DescriptivePaidUpsell/DescriptivePaidUpsell';
import UpsellActions from './components/UpsellActions/UpsellActions';

const usePaidDownloadUpsellModal = ({
    isOpen,
    handleClose,
}: {
    isOpen: boolean;
    handleClose: ModalCloseHandler;
}) => {
    const [persistentArtUrl, setPersistentArtUrl] = useState<string>('');
    const [isPremium, setIsPremium] = useState(false);
    const [persistentCreditsNeeded, setPersistentCreditsNeeded] =
        useState<number>(0);
    const {
        cancelPurchase,
        purchaseCostInCredits,
        item: downloadableItem,
    } = usePurchaseControllerContext();

    const { setIsEligibleForSweetener } = useSweetenerUpsellModalContext();

    const handleCloseWithAnalytics = (): void => {
        attemptedActionStore.remove();
        handleClose();
        cancelPurchase();
        setIsEligibleForSweetener(true);
    };

    const { userCredits = 0 } = useUserCreditsContext();

    useEffect(() => {
        if (downloadableItem?.artUrl) {
            setPersistentArtUrl(downloadableItem?.artUrl);
        }
    }, [downloadableItem?.artUrl]);

    useEffect(() => {
        if (downloadableItem?.isPremium !== undefined) {
            setIsPremium(downloadableItem?.isPremium);
        }
    }, [downloadableItem?.isPremium]);

    useEffect(() => {
        if (purchaseCostInCredits) {
            const creditsNeeded = purchaseCostInCredits - userCredits;

            setPersistentCreditsNeeded(creditsNeeded);
        }
    }, [purchaseCostInCredits, userCredits]);

    useStoreAttemptedDownloadAction(downloadableItem, isOpen);

    return {
        persistentCreditsNeeded,
        downloadableItem,
        persistentArtUrl,
        handleCloseWithAnalytics,
        isPremium,
    };
};

export const PaidDownloadUpsellModal: React.FC<ModalProps> = ({
    isOpen,
    handleClose,
}) => {
    const {
        persistentCreditsNeeded,
        handleCloseWithAnalytics,
        persistentArtUrl,
        isPremium,
    } = usePaidDownloadUpsellModal({ isOpen, handleClose });

    return (
        <SplitModal isOpen={isOpen} onClose={handleCloseWithAnalytics}>
            <DescriptivePaidUpsell
                isPremium={isPremium}
                creditsNeeded={persistentCreditsNeeded}
                artUrl={persistentArtUrl}
            />
            <UpsellActions creditsNeeded={persistentCreditsNeeded} />
        </SplitModal>
    );
};

export const FreeUserPaidDownloadUpsell = withInteractionSource(
    PaidDownloadUpsellModal,
    InteractionSource.UpsellModalFreeUserPaidDownload,
);

export const PaidUserPaidDownloadUpsell = withInteractionSource(
    PaidDownloadUpsellModal,
    InteractionSource.UpsellModalPaidUserPaidDownload,
);
